
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Pricing() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email: string) => {
    const blockedDomains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com'];
    const domain = email.split('@')[1]?.toLowerCase();
    return !blockedDomains.includes(domain);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'email') {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail(formData.email)) {
      setEmailError('Please use your company email address (not Gmail, Yahoo, Outlook, etc.)');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('email', formData.email);

      await fetch('https://readdy.ai/api/form/d3b6cnoig0ek50r8hi2g', {
        method: 'POST',
        body: formDataToSend
      });

      setShowConfirmation(true);
      setFormData({ name: '', company: '', email: '' });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showConfirmation) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <Header />
        <div className="flex items-center justify-center min-h-screen py-20">
          <div className="max-w-md mx-auto px-6 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-600 rounded-full mx-auto mb-6">
              <i className="ri-check-line text-2xl text-white"></i>
            </div>
            <h1 className="text-3xl font-bold mb-4">Thank You!</h1>
            <p className="text-gray-300 mb-8">
              Our team will contact you shortly at your email address with personalized pricing information.
            </p>
            <Button 
              variant="primary" 
              onClick={() => setShowConfirmation(false)}
            >
              Back to Pricing
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-gray-950">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Get Custom Pricing for Prismatics AI
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Fill in your details below. Our team will contact you back with personalized pricing based on your needs. Experience flexible plans designed to scale with your business growth and data complexity.
          </p>
        </div>
      </section>

      {/* Pricing Form */}
      <section className="py-20">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8">
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  required
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors text-sm"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                  Company *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Enter your company name"
                  required
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors text-sm"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Work Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your work email address"
                  required
                  className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none transition-colors text-sm ${
                    emailError ? 'border-red-500' : 'border-gray-700 focus:border-blue-500'
                  }`}
                />
                {emailError && (
                  <p className="mt-2 text-sm text-red-400">{emailError}</p>
                )}
                <p className="mt-2 text-sm text-gray-400">
                  Please use your company email address (not Gmail, Yahoo, Outlook, etc.)
                </p>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Get Pricing Quote
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Features Highlight */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">What's Included</h2>
            <p className="text-xl text-gray-300">
              Every Prismatics AI plan includes enterprise-grade features
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mx-auto mb-4">
                <i className="ri-shield-check-line text-xl text-white"></i>
              </div>
              <h3 className="font-bold mb-2">Enterprise Security</h3>
              <p className="text-gray-400 text-sm">SOC 2, GDPR compliant</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mx-auto mb-4">
                <i className="ri-customer-service-line text-xl text-white"></i>
              </div>
              <h3 className="font-bold mb-2">24/7 Support</h3>
              <p className="text-gray-400 text-sm">Dedicated success manager</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mx-auto mb-4">
                <i className="ri-links-line text-xl text-white"></i>
              </div>
              <h3 className="font-bold mb-2">Integrations</h3>
              <p className="text-gray-400 text-sm">Connect to 100+ data sources</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mx-auto mb-4">
                <i className="ri-rocket-line text-xl text-white"></i>
              </div>
              <h3 className="font-bold mb-2">Fast Deployment</h3>
              <p className="text-gray-400 text-sm">Up and running in days</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
